import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Pagination for the trust bar's mobile carousel.
 *
 * The carousel itself is native CSS scroll-snap, so swiping, keyboard scrolling
 * and the no-JS fallback all work without this class. All it adds is the dot
 * row: it mirrors the scroll position into `aria-current` and scrolls to a
 * slide when a dot is pressed.
 *
 * On desktop the dots are hidden by CSS and the observer simply never reports
 * a change, so there is nothing to tear down beyond the observer itself.
 *
 * @extends {Component<{
 *   scroller: HTMLElement,
 *   slides: HTMLElement[],
 *   dots: HTMLElement[],
 * }>}
 */
class TrustBarComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {AbortController | undefined} */
  #controller;

  /** Indices of the slides currently inside the scrollport. @type {Set<number>} */
  #visible = new Set();

  connectedCallback() {
    super.connectedCallback();

    const { scroller, slides } = this.refs;

    if (!(scroller instanceof HTMLElement) || !Array.isArray(slides) || slides.length < 2) return;

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: scroller,
      threshold: 0.6,
    });

    for (const slide of slides) this.#observer.observe(slide);

    this.#controller = new AbortController();

    document.addEventListener('shopify:block:select', this.#handleBlockSelect, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#controller?.abort();
    this.#controller = undefined;
    this.#visible.clear();
  }

  /**
   * Scrolls the carousel to the slide named by the pressed dot.
   *
   * @param {Event} event - The click event, delegated from `on:click`.
   */
  goToSlide(event) {
    const { slides } = this.refs;
    const target = event.target instanceof Element ? event.target.closest('[data-slide-index]') : null;

    if (!target || !Array.isArray(slides)) return;

    this.#scrollToSlide(slides[Number(target.getAttribute('data-slide-index'))]);
  }

  /**
   * Brings the block a merchant selected in the theme editor into view.
   *
   * @param {Event} event - The `shopify:block:select` event; its target is the block element.
   */
  #handleBlockSelect = (event) => {
    const { slides } = this.refs;
    const { target } = event;

    if (!(target instanceof HTMLElement) || !Array.isArray(slides)) return;

    this.#scrollToSlide(slides.find((slide) => slide === target || slide.contains(target)));
  };

  /** @param {Element | undefined} slide - The slide to bring to the start of the scrollport. */
  #scrollToSlide(slide) {
    const { scroller } = this.refs;

    if (!(slide instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

    // getBoundingClientRect rather than offsetLeft: the slides' offsetParent is
    // not guaranteed to be the scroller.
    const offset = slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left;

    scroller.scrollTo({
      left: scroller.scrollLeft + offset,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /** @type {IntersectionObserverCallback} */
  #handleIntersect = (entries) => {
    const { slides } = this.refs;

    if (!Array.isArray(slides)) return;

    for (const entry of entries) {
      const index = slides.indexOf(/** @type {HTMLElement} */ (entry.target));

      if (index === -1) continue;

      if (entry.isIntersecting) {
        this.#visible.add(index);
      } else {
        this.#visible.delete(index);
      }
    }

    if (this.#visible.size === 0) return;

    // With two slides in view the leading one owns the active dot, so the
    // marker never flip-flops between them mid-scroll.
    this.#setActiveDot(Math.min(...this.#visible));
  };

  /** @param {number} index - Index of the slide that owns the active dot. */
  #setActiveDot(index) {
    const { dots } = this.refs;

    if (!Array.isArray(dots)) return;

    for (const [position, dot] of dots.entries()) {
      dot.setAttribute('aria-current', position === index ? 'true' : 'false');
    }
  }
}

if (!customElements.get('trust-bar-component')) {
  customElements.define('trust-bar-component', TrustBarComponent);
}
